import React, { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Tabs, Tab, Modal, Button } from "react-bootstrap";
import IconButton from "components/common/iconButton";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import SubtleBadge from "components/common/subtleBadge";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { fetchRoleAndClaims, handleLoadingAndAccess } from "../../assets/js/common";
import { useNavigate, useLocation } from "react-router-dom";
import paths from "routes/paths";
import Invoices from "./invoices.js";
import { getDomainConfig } from "../../config/domainConfig";

const getBillingIntervalLabel = (subscriptionType, plans, t) => {
    const plan = plans.find(p => p.SubscriptionType === subscriptionType);
    const config = plan?.ProviderConfigs?.[0];
    if (!config?.BillingInterval) return "";

    const raw = config.BillingInterval.toLowerCase();
    if (raw.includes("month")) return t("Monthly");
    if (raw.includes("year")) return t("Yearly");
    if (raw.includes("week")) return t("Weekly");
    return config.BillingInterval; // fallback: show raw value
};

const CancelSubscriptionModal = ({ show, handleClose, onConfirm, confirmCancel, setConfirmCancel, subscriptionDetails, subscription, allPlans }) => {

    const { t } = useTranslation();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const handleConfirmClick = async () => {
        try {
            setConfirmLoading(true);
            await onConfirm();
            handleClose();
        } finally {
            setConfirmLoading(false);
        }
    };

    const getCancellationDate = () => {
        if (!subscription?.NextBillingDate) return "--";

        return moment.utc(subscription.NextBillingDate).local().format("DD-MM-YYYY");
    };
    return (
        <Modal show={show} onHide={handleClose} backdrop="static">
            <Modal.Header>
                <Modal.Title>{t("cancelSubscription")}</Modal.Title>
                <Button variant="close" onClick={handleClose} disabled={confirmLoading} />
            </Modal.Header>

            <Modal.Body>
                <p className="mb-3">
                    {t("cancelSubscriptionInfoText")}
                </p>

                <div className="border rounded p-3 bg-light mb-3">
                    <Row className="g-3">
                        {/* Left Side */}
                        <Col md={6}>
                            <div className="d-flex flex-column mb-3">
                                <span className="text-muted fw-medium font-size-13">
                                    {t("plan")}
                                </span>

                                <span className="fw-semibold font-size-13">
                                    {subscriptionDetails?.SubscriptionType
                                        ? t(`subscriptionTypeLable.${subscriptionDetails.SubscriptionType}`)
                                        : "--"}{" "}
                                    {getBillingIntervalLabel(subscriptionDetails?.SubscriptionType, allPlans, t)}
                                </span>
                            </div>

                            <div className="d-flex flex-column">
                                <span className="text-muted fw-medium font-size-13">
                                    {t("cancelDate")}
                                </span>

                                <span className="fw-semibold font-size-13">
                                    {moment().format("DD-MM-YYYY")}
                                </span>
                            </div>
                        </Col>

                        {/* Right Side */}
                        <Col md={6}>
                            <div className="d-flex flex-column mb-3">
                                <span className="text-muted fw-medium font-size-13">
                                    {t("numberOfUsers")}
                                </span>

                                <span className="fw-semibold font-size-13">
                                    {subscription?.NoOfUsers || "--"}
                                </span>
                            </div>
                        </Col>
                    </Row>
                </div>

                <div className="border bg-primary bg-opacity-10 rounded p-3 mb-3">
                    <div className="d-flex align-items-start">
                        <div className="me-2">
                            <i className="fas fa-info-circle text-primary opacity-75"></i>
                        </div>
                        <div>
                            <p className="mb-0 font-size-13">
                                <Trans
                                    i18nKey="subscriptionAccessText"
                                    values={{ date: getCancellationDate() }}
                                    components={{ bold: <strong /> }}
                                />
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="form-check d-flex align-items-center mb-0">
                        <input
                            type="checkbox"
                            id="confirmCancelSubscription"
                            checked={confirmCancel}
                            onChange={(e) => setConfirmCancel(e.target.checked)}
                            className="form-check-input me-2"
                        />
                        <label
                            htmlFor="confirmCancelSubscription"
                            className="form-check-label mb-0 mt-1"
                        >
                            <Trans
                                i18nKey="confirmCancelSubscriptionText"
                                components={{
                                    anchor: (
                                        <a
                                            href="/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary"
                                        />
                                    )
                                }}
                            />
                        </label>
                    </div>
                </div>

            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="falcon-default"
                    onClick={handleClose}
                    disabled={confirmLoading}
                >
                    {t("keepSubscription")}
                </Button>

                <Button
                    variant="danger"
                    onClick={handleConfirmClick}
                    disabled={!confirmCancel || confirmLoading}
                >
                    {confirmLoading ? (
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                            />
                            {t("processing")}
                        </>
                    ) : (
                        t("cancelSubscription")
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const Subscription = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [subscription, setSubscription] = useState(null);      // existing customer response
    const [subscriptionDetails, setSubscriptionDetails] = useState(null); // actual subscription
    const [businessTypes, setBusinessTypes] = useState([]);
    const [loadingSub, setLoadingSub] = useState(true);
    const customerId = localStorage.getItem("customerId");
    const [hasAccess, setHasAccess] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [allPlans, setAllPlans] = useState([]);
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [activeUserCount, setActiveUserCount] = useState(0);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [gatewayEnabled, setGatewayEnabled] = useState(true);
    const [showGatewayModal, setShowGatewayModal] = useState(false);
    const domainConfig = getDomainConfig();

    const paymentRoute = domainConfig.paymentRoute;
    const defaultTab =
        params.get("tab") === "billing"
            ? "billing"
            : "mySubacription";

    const [activeTab, setActiveTab] = useState(defaultTab);

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "mySubscription",
            claimValue: "view",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    const subscriptionTypeBadgeColors = {
        Trial: "info",
        Standard: "primary",
        Enterprise: "success",
        Basic: "warning"
    };

    const subscriptionStatusBadgeColors = {
        Active: "success",
        Inactive: "secondary",
        Cancelled: "danger",
        Blocked: "dark",
        PaymentWaiting: "warning",
        Trial: "info"
    };

    const fetchPlans = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/business/subscriptionRoute/all`,
                {
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );
            setAllPlans(res.data || []);
        } catch (err) {
            console.error("Failed to fetch plans", err);
        }
    };

    useEffect(() => {
        const checkGatewayStatus = async () => {
            try {
                const gatewayName = domainConfig.gatewayName?.toUpperCase();

                const resp = await axios.get(
                    `${process.env.REACT_APP_API_URL}/business/paymentGatewayConfigRoute/admin/all`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );

                const match = resp.data?.data?.find(
                    g => g.ProviderName?.toUpperCase() === gatewayName
                );
                setGatewayEnabled(Boolean(match?.IsEnabled));
            } catch (err) {
                console.error("Failed to check gateway status:", err);
                setGatewayEnabled(true);
            }
        };
        checkGatewayStatus();
    }, []);

    const fetchSubscriptionAndCount = async () => {
        try {
            const customerId = localStorage.getItem("customerId");

            const [countRes] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}/getActiveUsersCount`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                ),
            ]);

            const activeCount = countRes.data?.ActiveUserCount ?? 0;

            setActiveUserCount(activeCount);

        } catch (err) {
            setSubscription("");
        }
    };

    useEffect(() => {
        fetchPlans();
        fetchSubscriptionAndCount();
    }, []);

    const planType = subscriptionDetails?.SubscriptionType;
    const currentUsers = activeUserCount || 0;

    const trialPlanType = allPlans.find(p => p.SubscriptionCode?.startsWith("TRL"))?.SubscriptionType;
    const basicPlanType = allPlans.find(p => p.SubscriptionCode?.startsWith("BAS"))?.SubscriptionType;
    const standardPlanType = allPlans.find(p => p.SubscriptionCode?.startsWith("STD"))?.SubscriptionType;

    const basicPlan = allPlans.find(p => p.SubscriptionCode?.startsWith("BAS"));
    const basicMaxUsers = Number(basicPlan?.MaxUsers || 0);

    const isTrial = planType === trialPlanType;
    const isBasic = planType === basicPlanType;
    const isStandard = planType === standardPlanType;

    const isCancelled = subscription?.ProviderSubscriptionStatus?.toLowerCase() === "cancelled";

    const showUpgrade =
        isTrial || isBasic;

    const showCancel =
        (isBasic || isStandard) && !isCancelled;

    const showDowngrade = isStandard;

    useEffect(() => {
        const fetchSubscription = async () => {
            if (!customerId) {
                console.warn("No customerId found in URL or localStorage.");
                setLoadingSub(false);
                return;
            }

            try {
                // fetch customer by customerId
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`, {
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
                );
                setSubscription(res.data);

                if (res.data?.SubscriptionId) {
                    const subRes = await axios.get(
                        `${process.env.REACT_APP_API_URL}/business/subscriptionRoute/${res.data.SubscriptionId}`, {
                        headers: {
                            Authorization: process.env.REACT_APP_BasicAuthSecret
                        }
                    }
                    );
                    setSubscriptionDetails(subRes.data);
                }

                try {
                    const businessTypeRes = await axios.get(
                        `${process.env.REACT_APP_API_URL}/businessTypeRoute/${customerId}`,
                        {
                            headers: {
                                Authorization: process.env.REACT_APP_BasicAuthSecret
                            }
                        }
                    );
                    setBusinessTypes(businessTypeRes.data || []);
                } catch (btErr) {
                    console.error("Error fetching business types:", btErr);
                }

            } catch (err) {
                if (err.response?.status === 404) {
                    console.warn("Customer not found for customerId:", customerId);
                    setSubscription(null);
                } else {
                    console.error("Error fetching subscription:", err);
                    toast.error(t("failedToLoadData"));
                }
            } finally {
                setLoadingSub(false);
            }
        };

        fetchSubscription();
    }, [customerId, t]);

    const loadingOrRedirect = handleLoadingAndAccess(
        isLoading,
        hasAccess,
        paths.dashboard
    );

    if (loadingOrRedirect) {
        return loadingOrRedirect;
    }

    const cancelPaymentSubscription = async (customerId) => {
        await axios.post(
            `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/${paymentRoute}/cancelSubscription/byCustomerId/${customerId}`,
            null,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
    };

    // const sendCancellationEmail = (customerId, culture, previousType, subscription) => {

    //     const mappedInterval = intervalMap[subscription.SubscriptionInterval];

    //     axios.post(
    //         `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/subscriptionEmailRoute/cancelSubscription`,
    //         {
    //             customerId,
    //             culture,
    //             previousSubscriptionType: previousType,
    //             updatedSubscriptionType: "Trial",
    //             ...(mappedInterval && { subscriptionInterval: mappedInterval })
    //         },
    //         { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    //     ).catch(err => {
    //         console.error("Cancellation email failed (ignored):", err.message);
    //     });
    // };

    const sendCancellationEmail = (customerId, culture, previousType, subscription) => {
        // Derive from allPlans instead of a hardcoded map
        const intervalLabel = getBillingIntervalLabel(previousType, allPlans, t);

        axios.post(
            `${process.env.REACT_APP_PAYMENT_SERVICE_URL}/subscriptionEmailRoute/cancelSubscription`,
            {
                customerId,
                culture,
                previousSubscriptionType: previousType,
                subscriptionEndDate: subscription?.NextBillingDate || null,
                ...(intervalLabel && { subscriptionInterval: intervalLabel })
            },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        ).catch(err => {
            console.error("Cancellation email failed (ignored):", err.message);
        });
    };

    const fetchSubscriptionInfo = async (customerId) => {
        const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        let subscriptionDetails = null;

        if (res.data?.SubscriptionId) {
            const subRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/business/subscriptionRoute/${res.data.SubscriptionId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            subscriptionDetails = subRes.data;

        }

        return { subscription: res.data, subscriptionDetails };
    };

    const handleCancelSubscription = async () => {
        const culture = localStorage.getItem("culture");
        const previousSubscriptionType = subscriptionDetails.SubscriptionType;
        const localCustomerId = subscription.CustomerId;

        if (!localCustomerId) {
            toast.error(t("customerNotFound"));
            return;
        }

        try {
            await cancelPaymentSubscription(customerId);
            sendCancellationEmail(localCustomerId, culture, previousSubscriptionType, subscription);

            axios.post(
                `${process.env.REACT_APP_API_URL}/business/customerRoute/${localCustomerId}/logSubscriptionCancellation`,
                {
                    previousSubscriptionType,
                    performedByUserId: localStorage.getItem("UserId"),
                    performedByName: localStorage.getItem("UserName") || subscription?.CustomerName,
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            ).catch(err => console.error("Failed to log subscription cancellation:", err.message));

            toast.success(t("subscriptionCancelled"), { position: "top-right", autoClose: 5000 });

            const { subscription: updatedSub, subscriptionDetails: updatedDetails } =
                await fetchSubscriptionInfo(customerId);

            setSubscription(updatedSub);
            if (updatedDetails) setSubscriptionDetails(updatedDetails);

        } catch (error) {
            console.error("Cancel Subscription Error:", error);
            toast.error(t("failedToCancelSubscription"), { position: "top-right", autoClose: 5000 });
        }
    };

    const showButtons = showUpgrade || showCancel || showDowngrade;

    const DowngradeModal = ({ show, handleClose }) => {
        return (
            <Modal show={show} onHide={handleClose} backdrop="static">
                <Modal.Header>
                    <Modal.Title>{t("cannotSwitchPlan")}</Modal.Title>
                    <Button variant="close" onClick={handleClose} />
                </Modal.Header>

                <Modal.Body>
                    {t("reduceUsersMessage", { max: basicMaxUsers })}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("cancel")}
                    </Button>

                    <Button
                        variant="primary"
                        onClick={() => {
                            handleClose();
                            navigate("/users");
                        }}
                    >
                        {t("reduceUsers")}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <>
            <Row className="g-3 mb-3">
                <Col lg={12} xxl={12}>
                    <Card className="mb-3 h-100">
                        <Background image={corner4} className="bg-card" />
                        <Card.Body className="position-relative pe-3 pb-2">
                            <Row className="flex-between-center">
                                <Col md>
                                    <h5 className="mb-2 mb-md-0">{t("mySubscription")}</h5>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row className="mb-3 g-3">
                <Col md={12}>
                    <Card>
                        <Card.Body>
                            <Tabs
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(k)}
                                id="subscription-tabs"
                                transition={false}
                            >
                                <Tab
                                    eventKey="mySubacription"
                                    title={t("subscriptionDetails")}
                                    className="border-bottom border-x p-3"
                                >

                                    <>
                                        {showButtons && (
                                            <>
                                                <Row className="align-items-center mb-3">
                                                    <Col className="order-2 order-sm-1"></Col>

                                                    <Col xs="auto" className="mt-0 order-1 order-sm-2">

                                                        <div className="d-flex flex-wrap gap-2">

                                                            {/* Upgrade */}
                                                            {showUpgrade && (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        if (!gatewayEnabled) {
                                                                            setShowGatewayModal(true);
                                                                            return;
                                                                        }
                                                                        if (currentUsers > basicMaxUsers) {
                                                                            setShowDowngradeModal(true);
                                                                        } else {
                                                                            navigate("/subscription/renew-subscription");
                                                                        }
                                                                    }}
                                                                >
                                                                    {t("upgradeSubscription")}
                                                                </Button>
                                                            )}

                                                            {/* Switch to Basic */}
                                                            {showDowngrade && (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        if (!gatewayEnabled) {
                                                                            setShowGatewayModal(true);
                                                                            return;
                                                                        }
                                                                        if (currentUsers > basicMaxUsers) {
                                                                            setShowDowngradeModal(true);
                                                                        } else {
                                                                            navigate("/subscription/renew-subscription");
                                                                        }
                                                                    }}
                                                                >
                                                                    {t("switchToBasic")}
                                                                </Button>
                                                            )}

                                                            {/* Cancel */}
                                                            {showCancel && (
                                                                <IconButton
                                                                    variant="danger"
                                                                    size="sm"
                                                                    icon="times-circle"
                                                                    onClick={() => setShowCancelModal(true)}
                                                                >
                                                                    {t("cancelSubscription")}
                                                                </IconButton>
                                                            )}

                                                        </div>

                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </>

                                    {isCancelled && (
                                        <div className="border bg-warning bg-opacity-10 rounded p-3 mb-3">
                                            <div className="d-flex align-items-start">
                                                <div className="me-2">
                                                    <i className="fas fa-info-circle text-warning opacity-75"></i>
                                                </div>
                                                <div>
                                                    <p className="mb-0 mt-1 font-size-13">
                                                        <Trans
                                                            i18nKey="subscriptionCancelledBannerText"
                                                            values={{
                                                                date: subscription?.SubscriptionEndDate
                                                                    ? moment(subscription.SubscriptionEndDate).format("DD-MM-YYYY")
                                                                    : "--",
                                                            }}
                                                            components={{ bold: <strong /> }}
                                                        />
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {loadingSub ? (
                                        <div className="text-center my-5">
                                            <Spinner animation="border" role="status">
                                                <span className="visually-hidden">{t("loading")}</span>
                                            </Spinner>
                                        </div>
                                    ) : subscription ? (
                                        <>
                                            <Row className="g-3 mb-3">
                                                {/* Card 1: Personal Details */}
                                                <Col lg={6}>
                                                    <Card className="card-hover border-1 shadow-sm bg-light-subtle h-100">
                                                        <Card.Body>
                                                            <h6 className="fw-semi-bold ls fs-9 data-font-colour d-flex align-items-center gap-2 mb-2">
                                                                <div className="bg-primary-subtle text-primary rounded-2 d-inline-flex align-items-center justify-content-center p-2">
                                                                    <i className="fas fa-user-circle" />
                                                                </div>
                                                                {t("personalDetails")}
                                                            </h6>
                                                            <hr className="my-3" />
                                                            <div className="d-flex flex-column gap-3 mt-2">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("name")}</span>
                                                                    <span className="fw-bold fs-9">{subscription.CustomerName || "--"}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("phoneNumber")}</span>
                                                                    <span className="fw-bold fs-9">{subscription.PhoneNumber || "--"}</span>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>

                                                {/* Card 2: Account Information */}
                                                <Col lg={6}>
                                                    <Card className="card-hover border-1 shadow-sm bg-light-subtle h-100">
                                                        <Card.Body>
                                                            <h6 className="fw-semi-bold ls fs-9 data-font-colour d-flex align-items-center gap-2 mb-2">
                                                                <div className="bg-primary-subtle text-primary rounded-2 d-inline-flex align-items-center justify-content-center p-2">
                                                                    <i className="fas fa-address-card" />
                                                                </div>
                                                                {t("accountInformation")}
                                                            </h6>
                                                            <hr className="my-3" />
                                                            <div className="d-flex flex-column gap-3 mt-2">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("email")}</span>
                                                                    <span className="fw-bold fs-9">{subscription.Email || "--"}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("status")}</span>
                                                                    <SubtleBadge
                                                                        pill
                                                                        bg={subscriptionStatusBadgeColors[subscription.Status] || "secondary"}
                                                                        className="d-inline-flex align-items-center px-2 py-1"
                                                                    >
                                                                        <span className="mb-0 data-font-size">
                                                                            {t(`customerStatus.${subscription.Status}`)}
                                                                        </span>
                                                                    </SubtleBadge>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="g-3">
                                                {/* Card 3: Organization Details */}
                                                <Col lg={6}>
                                                    <Card className="card-hover border-1 shadow-sm bg-light-subtle h-100">
                                                        <Card.Body>
                                                            <h6 className="fw-semi-bold ls fs-9 data-font-colour d-flex align-items-center gap-2 mb-2">
                                                                <div className="bg-primary-subtle text-primary rounded-2 d-inline-flex align-items-center justify-content-center p-2">
                                                                    <i className="fas fa-building" />
                                                                </div>
                                                                {t("organizationDetails")}
                                                            </h6>
                                                            <hr className="my-3" />
                                                            <div className="d-flex flex-column gap-3 mt-2">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("organization")}</span>
                                                                    <span className="fw-bold fs-9">{subscription.OrganisationName || "--"}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("businesstype")}</span>
                                                                    <span className="fw-bold fs-9 text-end">
                                                                        {businessTypes.length > 0
                                                                            ? businessTypes
                                                                                .map((bt) => t(`businessTypeLabel.${bt.Name}`, { defaultValue: bt.Name }))
                                                                                .join(", ")
                                                                            : "--"}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("numberOfUsers")}</span>
                                                                    <span className="fw-bold fs-9">{subscription.NoOfUsers || "--"}</span>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>

                                                {/* Card 4: Subscription Details */}
                                                <Col lg={6}>
                                                    <Card className="card-hover border-1 shadow-sm bg-light-subtle h-100">
                                                        <Card.Body>
                                                            <h6 className="fw-semi-bold ls fs-9 data-font-colour d-flex align-items-center gap-2 mb-2">
                                                                <div className="bg-primary-subtle text-primary rounded-2 d-inline-flex align-items-center justify-content-center p-2">
                                                                    <i className="fas fa-credit-card" />
                                                                </div>
                                                                {t("subscriptionDetails")}
                                                            </h6>
                                                            <hr className="my-3" />
                                                            <div className="d-flex flex-column gap-3 mt-2">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("subscriptionType")}</span>
                                                                    <SubtleBadge
                                                                        pill
                                                                        bg={subscriptionTypeBadgeColors[subscriptionDetails?.SubscriptionType] || "secondary"}
                                                                        className="d-inline-flex align-items-center px-2 py-1"
                                                                    >
                                                                        <span className="mb-0 data-font-size">
                                                                            {t(`subscriptionTypeLable.${subscriptionDetails?.SubscriptionType}`)}
                                                                        </span>
                                                                    </SubtleBadge>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("subscriptionStatus")}</span>
                                                                    <SubtleBadge
                                                                        pill
                                                                        bg={
                                                                            isCancelled
                                                                                ? subscriptionStatusBadgeColors["Cancelled"] || "secondary"
                                                                                : subscriptionStatusBadgeColors[subscription.Status] || "secondary"
                                                                        }
                                                                        className="d-inline-flex align-items-center px-2 py-1"
                                                                    >
                                                                        <span className="mb-0 data-font-size">
                                                                            {isCancelled
                                                                                ? t("customerStatus.Cancelled")
                                                                                : t(`customerStatus.${subscription.Status}`)}
                                                                        </span>
                                                                    </SubtleBadge>
                                                                </div>
                                                                {getBillingIntervalLabel(subscriptionDetails?.SubscriptionType, allPlans, t) && (
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <span className="text-muted fs-9">{t("billingCycle")}</span>
                                                                        <span className="fw-bold fs-9">
                                                                            {getBillingIntervalLabel(subscriptionDetails?.SubscriptionType, allPlans, t)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("registeredDate")}</span>
                                                                    <span className="fw-bold fs-9">
                                                                        {subscription?.RegisteredDate ? moment(subscription.RegisteredDate).format("DD-MM-YYYY") : "--"}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("activationDate")}</span>
                                                                    <span className="fw-bold fs-9">
                                                                        {subscription?.ActivationDate ? moment(subscription.ActivationDate).format("DD-MM-YYYY") : "--"}
                                                                    </span>
                                                                </div>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-muted fs-9">{t("nextBillingDate")}</span>
                                                                    <span className="fw-bold fs-9">
                                                                        {!isCancelled && subscription?.NextBillingDate
                                                                            ? moment(subscription.NextBillingDate).format("DD-MM-YYYY")
                                                                            : "--"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        <p className="text-muted text-center py-3">{t("noSubscriptionData")}</p>
                                    )}

                                </Tab>
                                <Tab
                                    eventKey="billing"
                                    title={t("billing")}
                                    className="border-bottom border-x p-3"
                                >
                                    <Invoices customerId={customerId} />
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <CancelSubscriptionModal
                show={showCancelModal}
                handleClose={() => {
                    setShowCancelModal(false);
                    setConfirmCancel(false);
                }}
                onConfirm={handleCancelSubscription}
                confirmCancel={confirmCancel}
                setConfirmCancel={setConfirmCancel}
                subscriptionDetails={subscriptionDetails}
                subscription={subscription}
                allPlans={allPlans}
            />
            <DowngradeModal
                show={showDowngradeModal}
                handleClose={() => setShowDowngradeModal(false)}
            />

            <Modal
                show={showGatewayModal}
                onHide={() => setShowGatewayModal(false)}
                backdrop="static"
            >
                <Modal.Header>
                    <Modal.Title>{t("gatewayUnavailableTitle")}</Modal.Title>
                    <Button variant="close" onClick={() => setShowGatewayModal(false)} />
                </Modal.Header>

                <Modal.Body>

                    <p className="mb-0">{t("gatewayUnavailableMessage")}</p>

                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="falcon-default"
                        onClick={() => setShowGatewayModal(false)}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowGatewayModal(false);
                            window.open(
                                `${process.env.REACT_APP_WEBSITE_URL}?culture=${localStorage.getItem("culture") || "en"}`,
                                "_blank"
                            );
                        }}
                    >
                        {t("contactSupport")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
export default Subscription;